/*
 * Canonical metric eligibility & basis contract.
 *
 * One governed engine answering, for any derived efficiency metric over any
 * set of platform observations:
 *
 *   which observations are eligible for this metric?
 *   what numerator entered?  what denominator entered?  what spend entered?
 *   which platforms were excluded, and why?
 *   is the resulting blended value semantically comparable at all?
 *
 * Every blended rate a surface shows should come from `aggregateMetricBasis`
 * rather than dividing whole-scope totals. Whole-pool division is exactly how
 * a platform that cannot report a metric still ends up in its denominator —
 * CTV's 500k impressions dragging a blended CTR toward zero while its clicks
 * were never observations at all.
 *
 * This module generalizes the byte-pinned CTR/VVR eligibility engine
 * (platformEfficiencyMetric.js) to the full derived-metric vocabulary. The
 * platform-capability sets are imported from there — one definition of which
 * inventory is video-first, everywhere.
 *
 * Evidence semantics (the backend preserves these; this module depends on
 * them): a count is `null` when the source never reported it and `0` when the
 * source reported none. Only the second is an observation. An unreported
 * count excludes the observation from that metric; an observed zero
 * participates and legitimately moves the rate.
 */

import {
  CLICKLESS_PLATFORM_KEYS,
  VIDEO_RATE_CAPABLE_PLATFORM_KEYS,
  platformReportsCompatibleVideoViews,
  resolveVideoViewSemanticClass,
  platformReportsCompatibleVideoCompletions,
  resolveVideoCompletionSemanticClass,
  platformReportsCompatibleVideoStarts,
  resolveVideoStartSemanticClass,
  platformReportsCompatibleEngagements,
  resolveEngagementSemanticClass,
  SITE_EVENT_ENGAGEMENT_PLATFORM_KEYS,
} from "./platformEfficiencyMetric";

/* ------------------------------------------------------------------ */
/* Field reading                                                       */
/* ------------------------------------------------------------------ */

const reportedCount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/*
 * One alias table for the counts a basis can be built from. Client-billed
 * spend aliases win over the generic key, matching the governed topline
 * ladder; every read is null-preserving.
 */
const COUNT_FIELD_ALIASES = Object.freeze({
  spend: ["billedSpend", "clientFacingSpend", "spend"],
  impressions: ["impressions"],
  clicks: ["clicks"],
  engagements: ["engagements"],
  /* An explicit TrueView count outranks the legacy generic spelling. This is
     what prevents an older Google Ads `videoViews` value from replacing the
     governed Sheet count when both are present on a canonical observation. */
  videoViews: ["trueViewViews", "true_view_views", "videoViews", "video_views"],
  trueViewViews: ["trueViewViews", "true_view_views", "videoViews", "video_views"],
  videoStarts: ["videoStarts", "video_started", "videoStarted", "video_starts"],
  videoCompletions: [
    "videoCompletions",
    "video_completed",
    "videoCompleted",
    "video_completions",
    "completedViews",
    "completed_views",
  ],
  conversions: ["conversions"],
  revenue: ["revenue", "conversion_value", "conversionValue"],
});

export function readBasisCounts(observation = {}) {
  const source = observation?.metrics && typeof observation.metrics === "object"
    ? observation.metrics
    : observation;
  const counts = {};
  Object.entries(COUNT_FIELD_ALIASES).forEach(([field, aliases]) => {
    let resolved = null;
    for (const alias of aliases) {
      const candidate = reportedCount(source?.[alias]);
      if (candidate !== null) {
        resolved = candidate;
        break;
      }
    }
    counts[field] = resolved;
  });
  return counts;
}

/**
 * Which cost an observation's spend is stated in.
 *
 * Eligibility never depends on this — whether a platform reports clicks is not
 * a function of whose dollars are counted — so switching the reporting basis
 * moves the spend numerator and leaves the eligible set and every count
 * untouched. What the basis label is for is saying so: a CPC quoted on net
 * media cost and one quoted on client-billed spend are different numbers, and
 * a surface that shows either should be able to name which it has rather than
 * assume. `mixed` is reported, never silently averaged away, because summing a
 * net dollar with a gross one is exactly the error this contract exists to
 * prevent.
 */
export const SPEND_BASIS = Object.freeze({
  GROSS_CLIENT_BILLED: "GROSS_CLIENT_BILLED",
  NET_WORKING_MEDIA: "NET_WORKING_MEDIA",
});

const GROSS_SPEND_BASIS_ALIASES = new Set([
  "gross", "gross client billed", "client billed", "client facing",
  "client facing spend", "declared client billed", "planner pricing computed",
  "provider pass through", "commercial model derived", "report snapshot", "snapshot",
]);
const NET_SPEND_BASIS_ALIASES = new Set([
  "net", "net media", "working media", "raw media cost", "provider media cost",
]);

export function normalizeSpendBasis(value, fallback = SPEND_BASIS.GROSS_CLIENT_BILLED) {
  const raw = String(value == null ? "" : value).trim();
  if (!raw) return fallback;
  if (Object.values(SPEND_BASIS).includes(raw)) return raw;
  const normalized = raw.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (GROSS_SPEND_BASIS_ALIASES.has(normalized)) return SPEND_BASIS.GROSS_CLIENT_BILLED;
  if (NET_SPEND_BASIS_ALIASES.has(normalized)) return SPEND_BASIS.NET_WORKING_MEDIA;
  return raw;
}

export const DEFAULT_SPEND_BASIS = SPEND_BASIS.GROSS_CLIENT_BILLED;

export function readSpendBasis(observation = {}) {
  const source = observation?.metrics && typeof observation.metrics === "object"
    ? observation.metrics
    : observation;
  const declared = String(source?.spendBasis ?? source?.spend_basis ?? "").trim();
  return normalizeSpendBasis(declared, DEFAULT_SPEND_BASIS);
}

/* ------------------------------------------------------------------ */
/* Blend classification                                                */
/* ------------------------------------------------------------------ */

/*
 * How a metric may cross platform boundaries.
 *
 * ELIGIBILITY: additive counts with a capability gate — sum numerator and
 *   denominator across the platforms that actually report this metric, and
 *   only those. (CTR, CPC, CPM.)
 * SEMANTIC: the counts LOOK additive but their definitions differ by source —
 *   engagement taxonomies, attribution windows, completion definitions.
 *   Blending requires either a single participating source-definition or an
 *   explicit compatibility proof from the caller; otherwise the blended value
 *   is unavailable rather than manufactured. (VVR, CPV, ER, VCR, CPA, CVR,
 *   ROAS — a view, an engagement and a conversion are each defined by the
 *   source that reports them.)
 * SOURCE_SPECIFIC: never blended, reported per source only.
 */
export const METRIC_BLEND_POLICIES = Object.freeze({
  ELIGIBILITY: "eligibility",
  SEMANTIC: "semantic",
  SOURCE_SPECIFIC: "source_specific",
});

/* Exclusion reasons a surface may need to explain. */
export const BASIS_EXCLUSION_REASONS = Object.freeze({
  UNSUPPORTED: "unsupported",
  UNREPORTED: "unreported",
  MISSING_DENOMINATOR: "missing_denominator",
  DEFINITION_INCOMPATIBLE: "definition_incompatible",
});

/* ------------------------------------------------------------------ */
/* Metric registry                                                     */
/* ------------------------------------------------------------------ */

/*
 * Why a platform is outside a generic video-view basis. Two different facts,
 * and a surface that has to explain itself needs to tell them apart: Display
 * has no video contract at all, while CTV measures video carefully and simply
 * does not measure THIS — it reports starts, and its own KPI profile states
 * them. "Unsupported" would be true of Display and quietly wrong about CTV.
 */
const videoViewRefusal = (platformKey) =>
  VIDEO_RATE_CAPABLE_PLATFORM_KEYS.has(platformKey)
    ? BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE
    : BASIS_EXCLUSION_REASONS.UNSUPPORTED;

/*
 * Why a platform is outside the engagement basis. Same two facts as above:
 * inventory whose `engagements` column holds a landing-page start measures
 * something real that is not an ad engagement (DEFINITION_INCOMPATIBLE),
 * while inventory that reports no engagement at all — CTV, where the value is
 * a zero-fill — was never in the pool (UNSUPPORTED).
 */
const engagementRefusal = (platformKey) =>
  SITE_EVENT_ENGAGEMENT_PLATFORM_KEYS.has(platformKey)
    ? BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE
    : BASIS_EXCLUSION_REASONS.UNSUPPORTED;

const percent = (numerator, denominator) => (numerator / denominator) * 100;
const perThousand = (numerator, denominator) => (numerator / denominator) * 1000;
const ratio = (numerator, denominator) => numerator / denominator;

/*
 * The registry. `numeratorField`/`denominatorField` name basis counts;
 * `supports(platformKey)` is the capability rule (a platform whose inventory
 * cannot produce the numerator never enters, however its counts were seeded);
 * `numeratorRequired`/`denominatorRequired` demand the count be OBSERVED.
 *
 * Capability rulings preserved from the byte-pinned engine, not relitigated:
 * CLICKLESS inventory (CTV) never enters blended CTR/CPC — a click against a
 * non-clickable screen is not the same observation as a click on click
 * inventory, and the 0 sitting in its click field is a zero-fill. This is a
 * narrower ruling than "video-first": YouTube is read on a video rate and
 * still reports real clicks, so its clicks and the impressions that earned
 * them are honest input to a click rate.
 *
 * VCR denominator ruling (pinned here, deliberately): the canonical
 * completion-rate denominator is VIDEO STARTS. completions/views is a
 * different metric; a source reporting completions without starts is
 * MISSING_DENOMINATOR for blended VCR, never silently rebased onto views.
 */
export const METRIC_BASIS_DEFINITIONS = Object.freeze({
  ctr: {
    key: "ctr",
    label: "CTR",
    unit: "percent",
    blendPolicy: METRIC_BLEND_POLICIES.ELIGIBILITY,
    numeratorField: "clicks",
    denominatorField: "impressions",
    supports: (platformKey) => !CLICKLESS_PLATFORM_KEYS.has(platformKey),
    compute: percent,
    definition: "Clicks divided by impressions, across click-reporting platforms only.",
  },
  cpc: {
    key: "cpc",
    label: "CPC",
    unit: "currency",
    blendPolicy: METRIC_BLEND_POLICIES.ELIGIBILITY,
    numeratorField: "spend",
    denominatorField: "clicks",
    /* Spend is scoped to the click-eligible observations — CTV spend never
       enters a CPC merely because CTV spent. */
    supports: (platformKey) => !CLICKLESS_PLATFORM_KEYS.has(platformKey),
    /*
     * A click-eligible platform that reported NO clicks still spent the money.
     *
     * The absence/zero distinction runs both ways, and only one direction was
     * being honoured. Inventory that never reports clicks is correctly kept
     * out of the basis entirely — but a platform that DOES report clicks and
     * reported none of them is an observation, not an absence, and dropping it
     * takes its click-eligible spend out of the numerator with it. That makes
     * the campaign look cheaper per click than it was: a $500 line that
     * produced nothing simply stops counting, and the surviving platform's own
     * CPC is printed as the blend's.
     *
     * So a reported-zero denominator participates here. It cannot state a CPC
     * on its own — that is the aggregate's guard, which still withholds a
     * value when the whole contributing set clicked zero — but its cost
     * belongs to the pool that the clicks were bought from.
     */
    zeroDenominatorParticipates: true,
    compute: ratio,
    definition: "Client-billed spend per click, across click-reporting platforms only.",
  },
  cpm: {
    key: "cpm",
    label: "CPM",
    unit: "currency",
    blendPolicy: METRIC_BLEND_POLICIES.ELIGIBILITY,
    numeratorField: "spend",
    denominatorField: "impressions",
    /* Every delivering platform buys impressions; CPM legitimately blends
       across all of them. The gate is evidence, not inventory kind. */
    supports: () => true,
    compute: perThousand,
    definition: "Client-billed spend per 1,000 impressions across delivering platforms.",
  },
  /*
   * SEMANTIC, not ELIGIBILITY — a "video view" is not one measurement.
   *
   * Eligibility asks whether a platform can report the metric at all, and that
   * question was answered correctly here from the start: Display never entered
   * a Video View Rate. What eligibility cannot ask is whether two platforms
   * that both report a field called `videoViews` are counting the same event.
   * They are not. A CTV view is an impression whose playback began; YouTube
   * counts a view at thirty seconds or completion. Summing them and dividing
   * by their combined impressions produces a percentage that describes no
   * measurable thing, and it did: NSC's 69.50% was 119,309 CTV starts plus
   * 14,069 YouTube views over both platforms' impressions.
   *
   * These now blend only within one DECLARED measurement, or with proven
   * compatibility — the treatment `completionRate` and `costPerCompletedView`
   * have always had, for the same reason. Mixed-measurement campaigns state
   * the rate per platform rather than inventing a blended one.
   *
   * What that first fix left unfinished, and this completes: an ineligible
   * platform must be EXCLUDED from the basis, not allowed to invalidate it.
   * Declining the blend was right; declining it by nulling the whole metric
   * meant one platform that measures nothing comparable erased a rate the
   * platforms that DO measure it had reported perfectly well. On Never Stop
   * Chasing that read as a Video View Rate of "—" beside 146.8K video views,
   * which is not a more careful statement than a wrong number — it is the
   * absence of a right one.
   */
  vvr: {
    key: "vvr",
    label: "Video View Rate",
    unit: "percent",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "videoViews",
    denominatorField: "impressions",
    supports: platformReportsCompatibleVideoViews,
    semanticClass: resolveVideoViewSemanticClass,
    refusalReason: videoViewRefusal,
    compute: percent,
    definition:
      "Video views divided by the impressions of those same observations, across platforms that measure a view the same way.",
  },
  cpv: {
    key: "cpv",
    label: "CPV",
    unit: "currency",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "spend",
    denominatorField: "videoViews",
    supports: platformReportsCompatibleVideoViews,
    semanticClass: resolveVideoViewSemanticClass,
    refusalReason: videoViewRefusal,
    /* A view-measuring platform that measured none of them still spent the
       money — see `cpc`. Cost per outcome, not rate of outcome. */
    zeroDenominatorParticipates: true,
    compute: ratio,
    definition:
      "Client-billed spend on video-view-reporting platforms, per the views those same platforms measured.",
  },
  completionRate: {
    key: "completionRate",
    label: "Completion Rate",
    unit: "percent",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "videoCompletions",
    denominatorField: "videoStarts",
    supports: platformReportsCompatibleVideoCompletions,
    /*
     * Grouped by the DECLARED completion threshold, not by source identity.
     * Source identity was the placeholder while no threshold was declared,
     * and it is wrong in both directions here exactly as it was for `vvr`:
     * it separates two platforms measuring the same event through different
     * connectors, and it merges two thresholds the moment they arrive under
     * one source label.
     */
    semanticClass: resolveVideoCompletionSemanticClass,
    refusalReason: videoViewRefusal,
    compute: percent,
    definition:
      "Completed views divided by video starts, across platforms that measure a completion the same way.",
  },
  costPerCompletedView: {
    key: "costPerCompletedView",
    label: "Cost per Completed View",
    unit: "currency",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "spend",
    denominatorField: "videoCompletions",
    supports: platformReportsCompatibleVideoCompletions,
    semanticClass: resolveVideoCompletionSemanticClass,
    refusalReason: videoViewRefusal,
    /* A video platform that completed nothing still spent the money — see
       `cpc`. Note this is the COMPLETIONS denominator, not starts: CPCV has
       never needed a start to resolve (BE #218). */
    zeroDenominatorParticipates: true,
    compute: ratio,
    definition:
      "Client-billed spend per completed view, across platforms that measure a completion the same way.",
  },
  /*
   * DECLARED, like every other semantic metric — and it was the last one
   * still deciding by source identity.
   *
   * `supports: () => true` let any platform with a populated `engagements`
   * field into the basis, and the field is populated by different events on
   * different inventory: an ad-unit interaction on YouTube, a
   * `landing_page_starts` site event on StackAdapt display, a zero-fill on
   * CTV. Grouping by SOURCE then split YouTube from Display for the right
   * answer by the wrong reason, and would have merged two unlike events the
   * day they arrived under one connector.
   *
   * The refusal now distinguishes the two ways a platform can be outside the
   * basis, because they send a reader somewhere different: Display measures
   * something real that is not this, CTV measures nothing here at all.
   */
  engagementRate: {
    key: "engagementRate",
    label: "Engagement Rate",
    unit: "percent",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "engagements",
    denominatorField: "impressions",
    supports: platformReportsCompatibleEngagements,
    semanticClass: resolveEngagementSemanticClass,
    refusalReason: engagementRefusal,
    compute: percent,
    definition:
      "Engagements divided by the impressions of those same observations, across platforms that measure an engagement with the ad the same way. A landing-page start is a site event, not an ad engagement, and never enters.",
  },
  cpa: {
    key: "cpa",
    label: "CPA",
    unit: "currency",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "spend",
    denominatorField: "conversions",
    supports: () => true,
    /* A platform that converted nobody still spent the money — see `cpc`. */
    zeroDenominatorParticipates: true,
    compute: ratio,
    definition:
      "Client-billed spend per conversion. Attribution windows differ by source, so blending requires proven compatibility.",
  },
  conversionRate: {
    key: "conversionRate",
    label: "Conversion Rate",
    unit: "percent",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "conversions",
    denominatorField: "clicks",
    supports: (platformKey) => !CLICKLESS_PLATFORM_KEYS.has(platformKey),
    compute: percent,
    definition:
      "Conversions divided by clicks. Attribution windows differ by source, so blending requires proven compatibility.",
  },
  roas: {
    key: "roas",
    label: "ROAS",
    unit: "ratio",
    blendPolicy: METRIC_BLEND_POLICIES.SEMANTIC,
    numeratorField: "revenue",
    denominatorField: "spend",
    supports: () => true,
    compute: ratio,
    definition:
      "Revenue divided by client-billed spend. Revenue attribution differs by source, so blending requires proven compatibility.",
  },
});

export function resolveMetricBasisDefinition(metricKey) {
  return METRIC_BASIS_DEFINITIONS[String(metricKey || "")] || null;
}

/* ------------------------------------------------------------------ */
/* Participation                                                       */
/* ------------------------------------------------------------------ */

const normalizePlatformKey = (value) =>
  String(value == null ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

/**
 * May THIS observation enter THIS metric's basis — and if not, why not?
 *
 * The order of refusals is deliberate: an unsupported platform is excluded
 * before its evidence is examined (whatever counts it was seeded with), an
 * unobserved numerator excludes a supported platform, and a supported,
 * observed numerator without a usable denominator is a distinct fact a
 * surface may need to explain.
 */
export function resolveMetricParticipation(metricKey, observation = {}) {
  const definition = resolveMetricBasisDefinition(metricKey);
  if (!definition) return null;

  const platformKey = normalizePlatformKey(observation.platformKey ?? observation.key);
  const counts = readBasisCounts(observation);
  const numerator = counts[definition.numeratorField];
  const denominator = counts[definition.denominatorField];

  const refusal = (reason) => ({
    metricKey: definition.key,
    platformKey,
    eligible: false,
    reason,
    numerator: null,
    denominator: null,
    spend: null,
  });

  if (!definition.supports(platformKey)) {
    return refusal(
      definition.refusalReason
        ? definition.refusalReason(platformKey)
        : BASIS_EXCLUSION_REASONS.UNSUPPORTED
    );
  }
  if (numerator === null) {
    return refusal(BASIS_EXCLUSION_REASONS.UNREPORTED);
  }
  if (denominator === null || denominator < 0) {
    return refusal(BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR);
  }
  /*
   * A denominator of zero is two different facts, and the metric declares
   * which one applies to it.
   *
   * For a RATE, the numerator is a subset of the denominator — clicks of
   * impressions, views of impressions, completions of starts — so zero
   * denominator with any numerator is a contradiction, not an observation, and
   * the row is refused. That is the ruling that keeps a completion rate pinned
   * to starts instead of quietly rebasing onto views.
   *
   * For a COST PER OUTCOME, the numerator is spend, which stands in no such
   * relation to the outcome. Zero outcomes is a perfectly coherent
   * observation: money was spent and nothing came of it. Refusing that row
   * would remove its cost from a pool it genuinely belongs to.
   */
  if (denominator === 0 && !definition.zeroDenominatorParticipates) {
    return refusal(BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR);
  }
  if (numerator < 0) {
    return refusal(BASIS_EXCLUSION_REASONS.UNREPORTED);
  }

  return {
    metricKey: definition.key,
    platformKey,
    eligible: true,
    reason: null,
    numerator,
    denominator,
    /* The spend that rides along when this observation enters a spend-scoped
       basis — the observation's own spend, never the pool's. */
    spend: counts.spend,
    spendBasis: readSpendBasis(observation),
    /* The declared measurement these counts came from, when the metric has
       one. Null means the metric's compatibility falls back to source
       identity, which is still the honest default for the metrics whose
       definitions vary by connector rather than by inventory. */
    semanticClass: definition.semanticClass ? definition.semanticClass(platformKey) : null,
  };
}

/* ------------------------------------------------------------------ */
/* Aggregation                                                         */
/* ------------------------------------------------------------------ */

/**
 * Name the reason a basis has no value.
 *
 * "No data" is the one answer that is almost never true of these metrics. A
 * campaign whose Video View Rate is unavailable because CTV measures starts and
 * YouTube measures views has an abundance of data; what it lacks is a single
 * question those numbers jointly answer. A campaign whose CTR is unavailable
 * because every platform present is clickless inventory is a different fact
 * again, and a campaign where a click-reporting platform simply has not
 * reported yet is a third — that one will resolve itself, and the other two
 * never will. Collapsing all three into one label is how a governed refusal
 * gets read as a broken pipeline.
 *
 * The vocabulary is the exclusion vocabulary the basis already speaks, so a
 * surface has one set of reasons to translate and no new ones to invent.
 * Ordered by specificity: an incompatibility is the most precise thing that can
 * be said about an absent value, an unreported count the most actionable, and
 * `unsupported` the weakest — true only when nothing present could ever have
 * answered.
 */
const UNAVAILABLE_REASON_PRECEDENCE = [
  BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE,
  BASIS_EXCLUSION_REASONS.UNREPORTED,
  BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR,
  BASIS_EXCLUSION_REASONS.UNSUPPORTED,
];

/*
 * How each reason reads on a surface.
 *
 * This copy lives in the contract rather than in a page for the same reason
 * the arithmetic does: two portals rendering the same governed refusal in
 * different words is the same class of defect as two portals computing the
 * same rate differently. A reader comparing Cortex and Quantum on one campaign
 * should see one explanation.
 *
 * `label` is the KPI-slot replacement for a number — short, because it sits
 * where a figure would. `detail` is the sentence a tooltip, a report footnote
 * or a generated narrative can state, and deliberately says what is true of
 * the metric rather than what is wrong with the pipeline.
 */
export const BASIS_UNAVAILABLE_PRESENTATION = Object.freeze({
  [BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE]: Object.freeze({
    label: "Incompatible Definitions",
    detail:
      "The platforms reporting this metric do not measure it the same way, so there is no one rate they jointly state.",
  }),
  [BASIS_EXCLUSION_REASONS.UNREPORTED]: Object.freeze({
    label: "Not Reported",
    detail: "No platform eligible for this metric has reported it for this window yet.",
  }),
  [BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR]: Object.freeze({
    label: "Not Measurable",
    detail:
      "The eligible platforms reported none of the quantity this metric divides by, so the rate is undefined rather than low.",
  }),
  [BASIS_EXCLUSION_REASONS.UNSUPPORTED]: Object.freeze({
    label: "Not Supported",
    detail: "No platform delivering this campaign reports this metric.",
  }),
});

/* The label a surface shows when a governed basis has no number. Falls back to
   the historical wording only when there is genuinely nothing to explain —
   no basis at all, rather than a basis that declined. */
export const UNGOVERNED_UNAVAILABLE_LABEL = "No Data";

/**
 * Explain an absent value, for any surface that has a basis in hand.
 *
 * Returns null when the basis HAS a value: the caller should render the number.
 */
export function describeUnavailableBasis(basis) {
  if (!basis || typeof basis !== "object") return null;
  if (basis.value !== null && basis.value !== undefined) return null;

  const reason = basis.unavailableReason || null;
  const presentation = reason ? BASIS_UNAVAILABLE_PRESENTATION[reason] : null;
  if (!presentation) {
    return { reason, label: UNGOVERNED_UNAVAILABLE_LABEL, detail: null };
  }
  return { reason, label: presentation.label, detail: presentation.detail };
}

/**
 * Is this rendered KPI text a governed "no value" rather than a figure?
 *
 * Anything that compares two KPI snapshots needs this. Those comparisons used
 * to test for the literal string "No Data" to mean "nothing to compare here",
 * which silently became wrong the moment a refusal could say something more
 * specific: an unavailable rate would read as a CHANGED value and report a
 * fresh snapshot as stale.
 */
export function isGovernedUnavailableLabel(value) {
  const text = String(value === null || value === undefined ? "" : value).trim();
  if (!text) return false;
  if (text.toLowerCase() === UNGOVERNED_UNAVAILABLE_LABEL.toLowerCase()) return true;
  return Object.values(BASIS_UNAVAILABLE_PRESENTATION).some(
    (presentation) => presentation.label.toLowerCase() === text.toLowerCase()
  );
}

function finalizeBasisAvailability(basis) {
  if (basis.value !== null) {
    basis.unavailableReason = null;
    return basis;
  }

  if (!basis.comparable) {
    basis.unavailableReason = BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE;
    return basis;
  }

  /* Eligible contributors but still no value: they were folded and the
     denominator came to zero — spend with no clicks, delivery with no
     impressions. The evidence is present and the division is not defined. */
  if (basis.eligiblePlatformKeys.length) {
    basis.unavailableReason = BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR;
    return basis;
  }

  const reasons = new Set(basis.exclusions.map((entry) => entry.reason));
  basis.unavailableReason =
    UNAVAILABLE_REASON_PRECEDENCE.find((reason) => reasons.has(reason)) ||
    BASIS_EXCLUSION_REASONS.UNREPORTED;
  return basis;
}

/**
 * Fold a set of observations into one governed metric basis.
 *
 * observations: [{ platformKey, sourceKey?, metrics | flat counts }]
 * options.compatibility: for SEMANTIC metrics, the caller's proof that the
 *   participating definitions are comparable — `true` (proven compatible) or
 *   `false` (proven incompatible). When absent, compatibility is inferred:
 *   one distinct participating source-definition blends; more than one does
 *   not, and the value is unavailable rather than manufactured.
 *
 * The returned basis is explainable by construction: value, numerator,
 * denominator, which platforms entered, which were excluded and why.
 */
export function aggregateMetricBasis(metricKey, observations = [], options = {}) {
  const definition = resolveMetricBasisDefinition(metricKey);
  if (!definition) return null;

  const rows = Array.isArray(observations) ? observations : [];
  const eligible = [];
  const exclusions = [];

  rows.forEach((observation) => {
    const participation = resolveMetricParticipation(metricKey, observation);
    if (!participation) return;
    if (participation.eligible) {
      eligible.push({ participation, observation });
    } else if (participation.platformKey) {
      exclusions.push({ platformKey: participation.platformKey, reason: participation.reason });
    }
  });

  const basis = {
    metricKey: definition.key,
    label: definition.label,
    unit: definition.unit,
    blendPolicy: definition.blendPolicy,
    value: null,
    numerator: null,
    denominator: null,
    spend: null,
    /* Named only once spend actually folds — an empty or declined basis has no
       dollars, and therefore no cost basis to claim. */
    spendBasis: null,
    eligiblePlatformKeys: eligible.map((entry) => entry.participation.platformKey),
    excludedPlatformKeys: exclusions.map((entry) => entry.platformKey),
    exclusions,
    comparable: true,
    /* The measurement the value is stated on, once one is resolved. */
    semanticClass: null,
    /* Why there is no value, when there is no value. Set on the way out. */
    unavailableReason: null,
  };

  if (!eligible.length) return finalizeBasisAvailability(basis);

  /*
   * Semantic gate. A metric whose definitions differ blends only within one
   * measurement, or on the caller's explicit proof. Where the metric declares
   * a `semanticClass`, that declaration is the grouping key; otherwise the
   * participating source identity is, which remains the right default for
   * metrics that vary by connector (attribution windows, engagement
   * taxonomies) rather than by inventory.
   *
   * The gate GROUPS; it does not veto. Its job is to decide which observations
   * belong together, and a metric with one surviving group is answerable no
   * matter how many platforms failed to join it. Only a genuine ambiguity —
   * two or more groups, each able to produce its own valid value, with no
   * governed definition spanning them — leaves the metric unavailable, and
   * then it is reported as incomparable with every would-be participant named
   * so the surface can say WHY there is no number. Picking the largest group
   * would be inventing an answer the data does not contain.
   */
  let contributing = eligible;
  if (definition.blendPolicy === METRIC_BLEND_POLICIES.SEMANTIC) {
    const groupKeyOf = (entry) =>
      definition.semanticClass
        ? entry.participation.semanticClass
        : entry.observation.sourceKey ??
          entry.observation.definitionKey ??
          entry.participation.platformKey;

    const groups = new Map();
    eligible.forEach((entry) => {
      const groupKey = groupKeyOf(entry);
      const bucket = groups.get(groupKey) || [];
      bucket.push(entry);
      groups.set(groupKey, bucket);
    });

    const proven = options.compatibility;
    if (proven === false || (proven !== true && groups.size > 1)) {
      basis.comparable = false;
      basis.exclusions = [
        ...exclusions,
        ...eligible.map((entry) => ({
          platformKey: entry.participation.platformKey,
          reason: BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE,
        })),
      ];
      basis.excludedPlatformKeys = basis.exclusions.map((entry) => entry.platformKey);
      basis.eligiblePlatformKeys = [];
      return finalizeBasisAvailability(basis);
    }

    if (groups.size === 1) {
      const [[groupKey, members]] = [...groups.entries()];
      basis.semanticClass = groupKey;
      contributing = members;
    }
  }

  /* Numerator, denominator and spend all fold over the SAME contributing
     entries — the one construction that makes a co-scoped basis structural
     rather than something each caller has to remember. */
  const numerator = contributing.reduce((sum, entry) => sum + entry.participation.numerator, 0);
  const denominator = contributing.reduce((sum, entry) => sum + entry.participation.denominator, 0);
  const spendEntries = contributing
    .map((entry) => entry.participation.spend)
    .filter((value) => value !== null);

  /* The basis the spend above is stated in — one declared cost, or `mixed`
     when the contributors disagree, which a surface must not print as a
     single figure without saying so. */
  const spendBases = [
    ...new Set(
      contributing
        .filter((entry) => entry.participation.spend !== null)
        .map((entry) => entry.participation.spendBasis)
    ),
  ];

  basis.eligiblePlatformKeys = contributing.map((entry) => entry.participation.platformKey);
  basis.numerator = numerator;
  basis.denominator = denominator;
  basis.spend = spendEntries.length ? spendEntries.reduce((sum, value) => sum + value, 0) : null;
  basis.spendBasis =
    spendBases.length === 1 ? spendBases[0] : spendBases.length > 1 ? "mixed" : null;
  basis.value = denominator > 0 ? definition.compute(numerator, denominator) : null;
  return finalizeBasisAvailability(basis);
}

/**
 * Every registry metric folded over one observation set. The convenience for
 * surfaces that render a strip of blended metrics from one scope.
 */
/*
 * Provenance for the COUNTS, not just the rates.
 *
 * A derived rate that excludes a platform explains itself through its basis.
 * A count has no such record, and once a platform stops contributing to one,
 * the total simply gets smaller with nothing to say why. That is the reading
 * this change would otherwise invite: when CTV stops reporting views it never
 * measured, a campaign's Video Views total drops by most of its value, and a
 * total that shrinks without explanation looks like delivery that stopped.
 *
 * `complete` is the field that distinguishes the two. True means every
 * platform capable of the metric reported it, and the total covers the whole
 * eligible pool. False means the total is what the reporting platforms
 * reported — still correct, but a floor on the campaign rather than a census,
 * and surfaces should say so.
 */
export const COUNT_BASIS_DEFINITIONS = Object.freeze({
  /*
   * The COUNT and the RATE gate on the same declaration, deliberately. If a
   * platform's views are not fit to sit in the Video View Rate's numerator,
   * they are not fit to be counted as Video Views either — a total and a rate
   * that disagree about which observations are real is how 146.8K "video
   * views" ended up sitting beside a rate that refused to state itself.
   */
  videoViews: {
    key: "videoViews",
    label: "Video Views",
    countField: "videoViews",
    supports: platformReportsCompatibleVideoViews,
    semanticClass: resolveVideoViewSemanticClass,
    refusalReason: videoViewRefusal,
  },
  videoStarts: {
    key: "videoStarts",
    label: "Video Starts",
    countField: "videoStarts",
    supports: platformReportsCompatibleVideoStarts,
    semanticClass: resolveVideoStartSemanticClass,
    refusalReason: videoViewRefusal,
  },
  videoCompletions: {
    key: "videoCompletions",
    label: "Video Completions",
    countField: "videoCompletions",
    supports: platformReportsCompatibleVideoCompletions,
    semanticClass: resolveVideoCompletionSemanticClass,
    refusalReason: videoViewRefusal,
  },
  /*
   * The engagement COUNT gates on the same declaration as its rate, for the
   * reason the video counts already do: a total and a rate that disagree
   * about which observations are real is how a card comes to state 44,761
   * engagements beside a rate computed from 43,399 of them. The 1,362
   * landing-page starts and the CTV zero-fill are out of both, together.
   */
  engagements: {
    key: "engagements",
    label: "Engagements",
    countField: "engagements",
    supports: platformReportsCompatibleEngagements,
    semanticClass: resolveEngagementSemanticClass,
    refusalReason: engagementRefusal,
  },
});

export function aggregateCountBasis(countKey, observations = []) {
  const definition = COUNT_BASIS_DEFINITIONS[countKey];
  if (!definition) return null;

  const reporting = [];
  const exclusions = [];

  (Array.isArray(observations) ? observations : []).forEach((observation) => {
    const platformKey = normalizePlatformKey(
      observation && observation.platformKey !== undefined ? observation.platformKey : observation && observation.key
    );
    if (!platformKey) return;
    if (!definition.supports(platformKey)) {
      exclusions.push({
        platformKey,
        reason: definition.refusalReason
          ? definition.refusalReason(platformKey)
          : BASIS_EXCLUSION_REASONS.UNSUPPORTED,
      });
      return;
    }
    const value = readBasisCounts(observation)[definition.countField];
    if (value === null) {
      exclusions.push({ platformKey, reason: BASIS_EXCLUSION_REASONS.UNREPORTED });
      return;
    }
    reporting.push({
      platformKey,
      value,
      semanticClass: definition.semanticClass ? definition.semanticClass(platformKey) : null,
    });
  });

  /*
   * A count GROUPS by declared measurement, exactly as its rate does.
   *
   * `supports` only asks whether a platform measures the thing at all. It
   * cannot ask whether two platforms that do are counting the same event, and
   * for the whole video family they frequently are not. Summing across that
   * line is how a campaign reported 185,728 "video completions" — CTV's
   * 139,122 events at 95% added to YouTube's 46,606 at 100% — beside a 98.29%
   * rate computed from CTV alone. The pair was arithmetically impossible: it
   * implies 188,964 starts against a campaign that recorded 141,546.
   *
   * One surviving group states its total and names its class. Two or more
   * leave `value` null — a governed absence, not a smaller number — and the
   * per-group totals travel on `groups` so a surface can present each
   * measurement family in its own right rather than being forced to pick the
   * largest or invent a sum spanning both.
   */
  const groups = new Map();
  reporting.forEach((entry) => {
    const groupKey = entry.semanticClass === null ? entry.platformKey : entry.semanticClass;
    const bucket = groups.get(groupKey) || [];
    bucket.push(entry);
    groups.set(groupKey, bucket);
  });

  const groupSummaries = [...groups.entries()].map(([groupKey, entries]) => ({
    semanticClass: entries[0].semanticClass === null ? null : groupKey,
    value: entries.reduce((sum, entry) => sum + entry.value, 0),
    reportingPlatformKeys: entries.map((entry) => entry.platformKey),
  }));

  /*
   * A group contributing zero cannot make a total ambiguous.
   *
   * Ambiguity is about the VALUE: including or excluding a zero leaves the
   * sum identical, so there is nothing for a reader to misread and one class
   * still describes every event in the total. This is not a nicety — rows
   * written before completion counts became null-preserving keep a stored 0,
   * and without this rule YouTube's legacy `videoStarts: 0` would open a
   * second start "measurement" and withhold CTV's 141,546: a real total
   * destroyed by a row that reported nothing.
   */
  const contendingGroups = groupSummaries.filter((group) => group.value !== 0);
  const blendable = contendingGroups.length <= 1;
  const contributing = blendable ? reporting : [];

  return {
    countKey: definition.key,
    label: definition.label,
    value: contributing.length ? contributing.reduce((sum, entry) => sum + entry.value, 0) : null,
    /* The class that describes the events in the total — the one contending
       group when zeros were set aside, and otherwise the sole group's. */
    semanticClass: !blendable
      ? null
      : contendingGroups.length === 1
      ? contendingGroups[0].semanticClass
      : groupSummaries.length === 1
      ? groupSummaries[0].semanticClass
      : null,
    /* Always populated, including the single-group case — a surface that
       presents per-family never needs a second code path to find them. */
    groups: groupSummaries,
    reportingPlatformKeys: reporting.map((entry) => entry.platformKey),
    excludedPlatformKeys: exclusions.map((entry) => entry.platformKey),
    exclusions,
    /* Platforms that cannot measure the metric are not a gap in the count —
       whether they have no video contract at all (UNSUPPORTED) or measure
       something the count may not absorb (DEFINITION_INCOMPATIBLE). Only a
       platform that COULD have reported and did not leaves the total short of
       its eligible pool. */
    complete: exclusions.every((entry) => entry.reason !== BASIS_EXCLUSION_REASONS.UNREPORTED),
    /* Why there is no total, when there is no total. A count withheld because
       two measurements survive is a different fact from one nobody reported,
       and a surface must be able to say which. */
    unavailableReason: !reporting.length
      ? BASIS_EXCLUSION_REASONS.UNREPORTED
      : blendable
      ? null
      : BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE,
  };
}

/**
 * QUALIFIED VIDEO VIEWS — the cross-platform business KPI.
 *
 * Everything above this line refuses to add two provider events that measure
 * different things. That refusal is correct and stays correct: it is why
 * `videoViews` no longer absorbs a CTV start, and why `videoCompletions` no
 * longer publishes a 95%-threshold count added to a 100% one.
 *
 * But a campaign-level story still has to be tellable. Governing the counts
 * so tightly that CTV's 139,122 fully-watched trailers vanish from the video
 * headline replaces one wrong answer with another: a reader sees 28,135 and
 * concludes the campaign barely ran video, when video is most of what it
 * bought. Both failures come from the same assumption — that "video" must be
 * one number derived from one provider event.
 *
 * So this is a DECLARED BUSINESS METRIC, not a measurement claim. It states
 * that a TrueView view and a completed CTV view are both substantially
 * consumed video, counts one of each, and never pretends the underlying
 * events are the same thing. The distinction that makes it honest is the
 * threshold: a CTV START is playback beginning and does not qualify, which is
 * exactly the event whose mislabelling caused the original defect.
 *
 * The registry is the whole ruling. A platform participates only with an
 * explicit entry naming WHICH field is its qualified signal, so no platform
 * joins by accident — TikTok views, Meta 3-second views and OLV starts are
 * absent because nobody has ruled on them yet, not because they were
 * forgotten. Adding one is a governance decision made here, in one place,
 * rather than a condition spreading across surfaces.
 *
 * What this metric must never become is a rate. Summing the numerators is
 * defensible because both are qualified outcomes; dividing that sum by
 * anything is not, because the denominators measure different populations —
 * YouTube's impressions against CTV's starts. VVR and VCR stay native, per
 * platform, and are carried alongside each contribution rather than merged.
 */
export const QUALIFIED_VIDEO_VIEWS_DEFINITION_VERSION = "qualified_video_views_v2_trueview";

export const QUALIFIED_VIDEO_SIGNAL_REGISTRY = Object.freeze({
  youtube: Object.freeze({
    countField: "trueViewViews",
    nativeLabel: "TrueView Views",
    semanticClass: "youtube_trueview",
    rateMetricKey: "vvr",
    rateLabel: "VVR",
  }),
  ctv: Object.freeze({
    countField: "videoCompletions",
    nativeLabel: "Completed Views",
    semanticClass: "ctv_completed_view_95",
    rateMetricKey: "completionRate",
    rateLabel: "VCR",
  }),
  connected_tv: Object.freeze({
    countField: "videoCompletions",
    nativeLabel: "Completed Views",
    semanticClass: "ctv_completed_view_95",
    rateMetricKey: "completionRate",
    rateLabel: "VCR",
  }),
});

export const resolveQualifiedVideoSignal = (platformKey) => {
  const key = String(platformKey === null || platformKey === undefined ? "" : platformKey)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return QUALIFIED_VIDEO_SIGNAL_REGISTRY[key] || null;
};

export const platformContributesQualifiedVideoViews = (platformKey) =>
  resolveQualifiedVideoSignal(platformKey) !== null;

export function aggregateQualifiedVideoViews(observations = [], options = {}) {
  const list = Array.isArray(observations) ? observations : [];
  const contributions = [];
  const exclusions = [];

  list.forEach((observation) => {
    const platformKey = normalizePlatformKey(
      observation && observation.platformKey !== undefined
        ? observation.platformKey
        : observation && observation.key
    );
    if (!platformKey) return;

    const signal = resolveQualifiedVideoSignal(platformKey);
    if (!signal) {
      /* A platform with no ruling is UNSUPPORTED — it has not been judged and
         found wanting, it simply has no governed qualified signal yet. */
      exclusions.push({ platformKey, reason: BASIS_EXCLUSION_REASONS.UNSUPPORTED });
      return;
    }

    const counts = readBasisCounts(observation);
    const value = counts[signal.countField];
    if (value === null) {
      /*
       * No fallback, ever. CTV reporting starts but not completions
       * contributes NOTHING — substituting the start count is precisely the
       * defect this metric is built beside, and it would re-enter here as a
       * convenience if the absence were allowed to resolve to something else.
       */
      exclusions.push({ platformKey, reason: BASIS_EXCLUSION_REASONS.UNREPORTED });
      return;
    }

    /* The platform's own native rate, computed on its own observation alone,
       so a contribution never borrows a neighbour's denominator. */
    const rateBasis = aggregateMetricBasis(signal.rateMetricKey, [observation], options);

    contributions.push({
      platformKey,
      countField: signal.countField,
      nativeLabel: signal.nativeLabel,
      semanticClass: signal.semanticClass,
      value,
      /* The cost of THIS contribution, read through the same client-billed
         alias ladder as every other spend on the page. It rides with the
         count rather than being looked up again later, so a cost per
         qualified view cannot fold a numerator over a different set of
         observations than the denominator it divides. */
      spend: counts.spend,
      spendBasis: readSpendBasis(observation),
      rate:
        rateBasis && rateBasis.value !== null
          ? {
              metricKey: signal.rateMetricKey,
              label: signal.rateLabel,
              value: rateBasis.value,
              numerator: rateBasis.numerator,
              denominator: rateBasis.denominator,
            }
          : null,
    });
  });

  return {
    metricKey: "qualifiedVideoViews",
    label: "Qualified Video Views",
    definitionVersion: QUALIFIED_VIDEO_VIEWS_DEFINITION_VERSION,
    definition:
      "Each video platform's governed qualified viewing signal, summed: YouTube TrueView views and CTV completed views. A CTV start is playback beginning, not consumption, and never qualifies.",
    value: contributions.length
      ? contributions.reduce((sum, entry) => sum + entry.value, 0)
      : null,
    contributions,
    contributingPlatformKeys: contributions.map((entry) => entry.platformKey),
    excludedPlatformKeys: exclusions.map((entry) => entry.platformKey),
    exclusions,
    /* A platform with a governed signal that reported nothing leaves the
       total short; one with no ruling at all was never part of the pool. */
    complete: exclusions.every((entry) => entry.reason !== BASIS_EXCLUSION_REASONS.UNREPORTED),
    unavailableReason: contributions.length ? null : BASIS_EXCLUSION_REASONS.UNREPORTED,
  };
}

/**
 * COST PER QUALIFIED VIDEO VIEW — what the qualified video above cost.
 *
 * The metric directly above declares that a TrueView view and a completed CTV
 * view are both substantially consumed video and counts one of each. This
 * states what the campaign paid for one of them, and it exists because the
 * campaign strip now headlines a number it could not price: CTV is routinely
 * half a video flight's spend and is structurally absent from CPC, so a strip
 * carrying Clicks, CTR and CPC beside a qualified video total priced barely
 * half the money it was reporting on.
 *
 * WHY THIS IS A COST AND NOT THE RATE THE METRIC ABOVE REFUSES
 *
 * `aggregateQualifiedVideoViews` deliberately exposes no `rate`, and that
 * ruling is untouched here. A RATE divides the qualified sum by a population,
 * and there is no single population to divide by — YouTube's denominator is
 * impressions and CTV's is starts, so the quotient would describe nothing.
 * A COST divides spend by the outcome, and spend stands in no such relation
 * to the population that produced it. This is CPA's shape, not VVR's:
 * conversions are likewise defined differently by every platform, and cost
 * per conversion has always been a defensible campaign reading of them.
 *
 * What it inherits and does not compound: the unit is a QUALIFIED VIDEO
 * OUTCOME, blended by declaration. It is honest only for as long as the
 * registry's entries stay defensible as one business event, which is the same
 * condition the count above already lives under.
 *
 * CO-SCOPING IS STRUCTURAL, NOT REMEMBERED
 *
 * The numerator folds over exactly the contributions the denominator is made
 * of — each carries its own spend — so there is no way to write a version of
 * this that charges the campaign's display dollars against video they never
 * produced. On live NSC that is $12,402.66 over 176,703, not $14,370.21: the
 * $1,967.55 of display spend is not in the numerator because display is not
 * in the denominator.
 *
 * The absence/zero distinction runs the same way it does for `cpc`. A
 * qualified-video platform that reported ZERO qualified views still spent the
 * money and its cost belongs in the pool — a zero is an observation, and it
 * arrives here as a contribution like any other. A platform that reported
 * NOTHING is an absence: it is excluded from both halves and leaves
 * `complete: false` on the count above, because adding its spend to a
 * numerator whose matching views never arrived would price the campaign
 * against video it cannot show.
 */
export const COST_PER_QUALIFIED_VIDEO_VIEW_KEY = "costPerQualifiedVideoView";
export const COST_PER_QUALIFIED_VIDEO_VIEW_LABEL = "CPQV";
export const COST_PER_QUALIFIED_VIDEO_VIEW_LONG_LABEL = "Cost per Qualified Video View";
export const COST_PER_QUALIFIED_VIDEO_VIEW_DEFINITION =
  "Client-billed spend per qualified video view — the spend of the platforms that contributed qualified video, divided by the qualified views they contributed. Spend from platforms with no qualified video signal is not in the numerator.";

export function aggregateCostPerQualifiedVideoView(observations = [], options = {}) {
  const qualified = aggregateQualifiedVideoViews(observations, options);
  const contributions = qualified.contributions;
  const missingSpend = contributions.some((entry) => entry.spend === null);

  const spendEntries = contributions
    .map((entry) => entry.spend)
    .filter((value) => value !== null);
  const spendBases = [
    ...new Set(
      contributions.filter((entry) => entry.spend !== null).map((entry) => entry.spendBasis)
    ),
  ];

  const numerator = contributions.length && !missingSpend
    ? spendEntries.reduce((sum, value) => sum + value, 0)
    : null;
  const denominator = qualified.value;

  /*
   * Three distinct absences, named apart because they send a reader to three
   * different places. No qualified video at all is the count's own absence.
   * Qualified video with no spend behind it is a reporting gap on the cost
   * side. And qualified views that summed to zero is the guard that keeps a
   * campaign which delivered none from reading "$0.00" — money was spent, the
   * outcome was not produced, and a cost per outcome does not exist.
   */
  const unavailableReason =
    !contributions.length
      ? BASIS_EXCLUSION_REASONS.UNREPORTED
      : numerator === null
      ? BASIS_EXCLUSION_REASONS.UNREPORTED
      : denominator > 0
      ? null
      : BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR;

  return {
    metricKey: COST_PER_QUALIFIED_VIDEO_VIEW_KEY,
    label: COST_PER_QUALIFIED_VIDEO_VIEW_LABEL,
    longLabel: COST_PER_QUALIFIED_VIDEO_VIEW_LONG_LABEL,
    definition: COST_PER_QUALIFIED_VIDEO_VIEW_DEFINITION,
    definitionVersion: QUALIFIED_VIDEO_VIEWS_DEFINITION_VERSION,
    unit: "unit_cost",
    value: unavailableReason === null ? numerator / denominator : null,
    numerator,
    denominator,
    spend: numerator,
    spendBasis: spendBases.length === 1 ? spendBases[0] : spendBases.length > 1 ? "mixed" : null,
    /* The same platform set as the count, by construction — stated so a
       surface can name who is priced without re-deriving the scope. */
    contributingPlatformKeys: qualified.contributingPlatformKeys,
    excludedPlatformKeys: qualified.excludedPlatformKeys,
    exclusions: qualified.exclusions,
    /* The count's completeness is the cost's completeness: a numerator whose
       matching views are missing is not a cheaper campaign. */
    complete: qualified.complete,
    unavailableReason,
  };
}

/*
 * A count and the rate computed from it must be able to coexist.
 *
 * Never Stop Chasing published "Video Completions 0" beside "Video Completion
 * Rate 98.31%". Both figures were individually defensible — the rate came from
 * CTV's own governed basis, the count from a withheld blend that a `|| 0`
 * coerced into a number — and together they described a campaign that cannot
 * exist. A reader cannot reconcile them, and the pair discredits both.
 *
 * The rule is narrow on purpose: it fires only where the count is ZERO or
 * ABSENT while the rate is POSITIVE, which is the arithmetically impossible
 * corner. It does not touch a zero count beside a zero rate, or a count the
 * source genuinely reports as zero alongside a rate the source itself also
 * reports — `sourceReportsBoth` is how a real provider anomaly stays visible
 * instead of being silently reshaped into something tidier.
 *
 * Returns which of the pair may be stated. Nothing is invented and nothing is
 * back-filled: an unreconcilable rate is WITHHELD, never recomputed from a
 * count that was not its denominator.
 */
export function reconcileCountWithRate({
  count = null,
  rate = null,
  sourceReportsBoth = false,
} = {}) {
  const countNumber = count === null || count === undefined || count === "" ? null : Number(count);
  const rateNumber = rate === null || rate === undefined || rate === "" ? null : Number(rate);
  const resolvedCount = Number.isFinite(countNumber) ? countNumber : null;
  const resolvedRate = Number.isFinite(rateNumber) ? rateNumber : null;

  const impossible = resolvedRate !== null && resolvedRate > 0 && (resolvedCount === null || resolvedCount === 0);

  if (!impossible) {
    return { count: resolvedCount, rate: resolvedRate, reconciled: true, anomaly: false, reason: null };
  }

  if (sourceReportsBoth) {
    /* The provider really did report this pair. It stays visible and is
       labelled an anomaly, because hiding a source's contradiction is how the
       contradiction reaches a client's own dashboard unexplained. */
    return {
      count: resolvedCount,
      rate: resolvedRate,
      reconciled: false,
      anomaly: true,
      reason: "source_reported_zero_count_with_positive_rate",
    };
  }

  return {
    count: resolvedCount,
    rate: null,
    reconciled: false,
    anomaly: false,
    reason:
      resolvedCount === null
        ? "rate_without_reported_count"
        : "rate_without_contributing_count",
  };
}
/**
 * A COUNT AND THE RATE BUILT FROM IT, STATED ON ONE POPULATION.
 *
 * `reconcileCountWithRate` below catches the pair that cannot both be true. It
 * cannot repair one, because by the time it runs the two halves have already
 * been resolved by two different engines asking two different questions:
 *
 *   aggregateCountBasis("videoCompletions") asks MAY THESE TOTALS BE SUMMED.
 *   aggregateMetricBasis("completionRate")  asks WHO HAS BOTH HALVES.
 *
 * On Never Stop Chasing those questions have different answers, and neither
 * answer is wrong. CTV reports 165,084 completions at a 95% threshold against
 * 167,806 starts. YouTube reports 52,901 played-to-100% and no starts at all.
 * The COUNT sees two contending measurements and withholds a total. The RATE
 * drops YouTube for want of a denominator, is left with one class, and states
 * 98.38%. The strip then prints a governed rate beside "No Data", and a reader
 * is asked to believe a campaign completed 98.38% of a quantity nobody counted.
 *
 * The repair is not to loosen either engine. It is to stop asking twice. When
 * the rate resolves it has ALREADY folded a numerator over one declared
 * measurement — 165,084, sitting unused in `basis.numerator` while the card
 * above it said nothing was available. That numerator IS the count, on exactly
 * the population that produced the rate, and pairing them is therefore not a
 * new claim: it is the claim the rate was already making, printed in full.
 *
 * What the pair must never do is hide the platform it left behind. YouTube's
 * 52,901 completions are real; they are simply not the same event, so they are
 * named in `excluded` and the pair reports `complete: false` rather than
 * letting 165,084 pass for the campaign's whole completion story.
 *
 * The forbidden corner is unreachable by construction. A positive rate has a
 * positive numerator, and the numerator is the count.
 */
const COUNT_RATE_PAIR_DEFINITIONS = Object.freeze({
  videoCompletions: Object.freeze({
    pairKey: "videoCompletions",
    countKey: "videoCompletions",
    rateMetricKey: "completionRate",
  }),
  /* Views and VVR have never disagreed on live data — one class survives both
     engines — which is precisely why the pair is declared for them too. The
     asymmetry that broke completions was invisible until a second measuring
     platform arrived, and it would arrive here the same way. */
  videoViews: Object.freeze({
    pairKey: "videoViews",
    countKey: "videoViews",
    rateMetricKey: "vvr",
  }),
  /*
   * Engagements and Engagement Rate sit side by side on every campaign strip,
   * so they answer to the pair for the same reason the completion cards do.
   * Without it the count is a cross-measurement sum and the rate is one
   * platform's — a reader cannot divide the first by anything to reach the
   * second, and the two together imply a campaign that does not exist.
   */
  engagements: Object.freeze({
    pairKey: "engagements",
    countKey: "engagements",
    rateMetricKey: "engagementRate",
  }),
});

/* Who reported this count, and under which declared measurement — flattened
   out of the count basis's groups so a pair can name the platforms its own
   population excluded without re-reading the observations. */
const countReportersByPlatform = (countBasis) => {
  const groups = countBasis && Array.isArray(countBasis.groups) ? countBasis.groups : [];
  const reporters = [];
  groups.forEach((group) => {
    (group.reportingPlatformKeys || []).forEach((platformKey) => {
      reporters.push({ platformKey, semanticClass: group.semanticClass, value: group.value });
    });
  });
  return reporters;
};

export function aggregateCountRatePair(pairKey, observations = [], options = {}) {
  const definition = COUNT_RATE_PAIR_DEFINITIONS[String(pairKey || "")];
  if (!definition) return null;

  const countBasis = aggregateCountBasis(definition.countKey, observations);
  const rateBasis = aggregateMetricBasis(definition.rateMetricKey, observations, options);
  const countLabel = countBasis ? countBasis.label : null;
  const rateLabel = rateBasis ? rateBasis.label : null;

  const shell = {
    pairKey: definition.pairKey,
    countKey: definition.countKey,
    rateMetricKey: definition.rateMetricKey,
    countLabel,
    rateLabel,
    /* The one population both halves are stated on. A surface that prints the
       pair prints this beside it; a surface that cannot is printing two
       numbers whose relationship it has not established. */
    platformKeys: [],
    semanticClass: null,
    count: null,
    rate: null,
    excluded: [],
    complete: true,
    unavailableReason: null,
  };

  if (rateBasis && rateBasis.value !== null) {
    const platformKeys = rateBasis.eligiblePlatformKeys.slice();
    const inPopulation = new Set(platformKeys);
    /* Reported the count, but not from inside the population the rate was
       folded over — a different declared measurement, or a platform with no
       denominator. Its count is real and it is not in this total. */
    const shortBy = countReportersByPlatform(countBasis)
      .filter((entry) => !inPopulation.has(entry.platformKey))
      .map((entry) => ({
        platformKey: entry.platformKey,
        semanticClass: entry.semanticClass,
        value: entry.value,
        reason: BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE,
      }));

    /*
     * Platforms the count basis refused at the capability gate — inventory
     * that measures something else under this field name, or measures nothing
     * here at all. They belong in `excluded` because a surface explaining the
     * population must be able to name them, and they must NOT touch
     * `complete`: a platform whose signal was never this metric cannot leave
     * the metric short. Only a declared contributor sitting outside the
     * chosen population does that.
     */
    const gateRefused = ((countBasis && countBasis.exclusions) || [])
      .filter((entry) => !inPopulation.has(entry.platformKey))
      .filter((entry) => !shortBy.some((row) => row.platformKey === entry.platformKey))
      .map((entry) => ({
        platformKey: entry.platformKey,
        semanticClass: null,
        value: null,
        reason: entry.reason,
      }));

    return {
      ...shell,
      scope: "rate_basis",
      platformKeys,
      semanticClass: rateBasis.semanticClass,
      count: rateBasis.numerator,
      rate: rateBasis.value,
      rateNumerator: rateBasis.numerator,
      rateDenominator: rateBasis.denominator,
      excluded: [...shortBy, ...gateRefused],
      complete: shortBy.length === 0,
      unavailableReason: null,
    };
  }

  /*
   * No rate. The count may still be stateable on its own — a total with no
   * rate is an ordinary fact, and it is the reverse pairing that is
   * impossible. What is not permitted here is reaching for a rate the engine
   * declined: an unavailable rate stays unavailable.
   */
  if (countBasis && countBasis.value !== null) {
    return {
      ...shell,
      scope: "count_basis",
      platformKeys: countBasis.reportingPlatformKeys.slice(),
      semanticClass: countBasis.semanticClass,
      count: countBasis.value,
      rate: null,
      rateNumerator: null,
      rateDenominator: null,
      excluded: (countBasis.exclusions || []).map((entry) => ({
        platformKey: entry.platformKey,
        semanticClass: null,
        value: null,
        reason: entry.reason,
      })),
      complete: countBasis.complete,
      unavailableReason: null,
    };
  }

  return {
    ...shell,
    scope: "unavailable",
    count: null,
    rate: null,
    rateNumerator: null,
    rateDenominator: null,
    excluded: (countBasis && countBasis.exclusions ? countBasis.exclusions : []).map((entry) => ({
      platformKey: entry.platformKey,
      semanticClass: null,
      value: null,
      reason: entry.reason,
    })),
    complete: countBasis ? countBasis.complete : true,
    /* The count's reason, not the rate's: the count is what the slot was
       going to show, so it is the absence a reader needs explained. */
    unavailableReason:
      (countBasis && countBasis.unavailableReason) ||
      (rateBasis && rateBasis.unavailableReason) ||
      BASIS_EXCLUSION_REASONS.UNREPORTED,
  };
}

export function aggregateAllCountRatePairs(observations = [], options = {}) {
  const pairs = {};
  Object.keys(COUNT_RATE_PAIR_DEFINITIONS).forEach((pairKey) => {
    pairs[pairKey] = aggregateCountRatePair(pairKey, observations, options);
  });
  return pairs;
}

export function aggregateAllCountBases(observations = []) {
  const bases = {};
  Object.keys(COUNT_BASIS_DEFINITIONS).forEach((countKey) => {
    bases[countKey] = aggregateCountBasis(countKey, observations);
  });
  return bases;
}

export function aggregateAllMetricBases(observations = [], options = {}) {
  const bases = {};
  Object.keys(METRIC_BASIS_DEFINITIONS).forEach((metricKey) => {
    bases[metricKey] = aggregateMetricBasis(metricKey, observations, options);
  });
  return bases;
}
